import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import Logo from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { formatApiError } from "../lib/api";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const { t, lang, setLang } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Brand panel */}
      <div className="hidden md:flex flex-col justify-between p-10 text-white"
           style={{ background: "linear-gradient(160deg, #0B192C 0%, #142944 60%, #7A1B28 130%)" }}
           data-testid="login-brand-panel">
        <Logo size={56} />
        <div className="max-w-md">
          <div className="eyebrow text-[#F1B8C0] mb-4">{t("tagline")}</div>
          <h1 className="font-display text-4xl md:text-5xl leading-tight font-semibold tracking-tight">
            {t("appName")}
          </h1>
          <p className="mt-4 text-white/70 text-sm leading-relaxed">
            Manage student journeys from first inquiry to enrolment across
            Georgia, Armenia, Belarus, Uzbekistan, Ukraine, Russia, Moldova,
            Romania, Tajikistan, China and Rwanda — all in one place.
          </p>
        </div>
        <div className="text-xs text-white/50">© {new Date().getFullYear()} MER Educational Consultancy</div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="flex md:hidden mb-6"><Logo withWordmark /></div>
          <div className="mb-8">
            <h2 className="font-display text-2xl font-semibold tracking-tight text-[#0B192C]">
              {t("signIn")}
            </h2>
            <p className="text-sm text-slate-500 mt-1">{t("signInSubtitle")}</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-600">
                {t("email")}
              </Label>
              <Input id="email" type="email" value={email} required
                     onChange={(e) => setEmail(e.target.value)}
                     data-testid="login-email"
                     className="mt-1" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-600">
                {t("password")}
              </Label>
              <Input id="password" type="password" value={password} required
                     onChange={(e) => setPassword(e.target.value)}
                     data-testid="login-password"
                     className="mt-1" />
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                   data-testid="login-error">
                {error}
              </div>
            )}

            <Button type="submit" disabled={loading}
                    className="w-full bg-[#7A1B28] hover:bg-[#601520] text-white"
                    data-testid="login-submit">
              {loading && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
              {t("signIn")}
            </Button>

            <button type="button" onClick={() => setLang(lang === "en" ? "ar" : "en")}
                    className="text-xs text-slate-500 hover:text-[#7A1B28] w-full text-center pt-2"
                    data-testid="login-lang-toggle">
              {lang === "en" ? "التبديل إلى العربية" : "Switch to English"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
